import { useCallback, useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, UserPlus, KeyRound, ShieldCheck } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", role_id: "", branch_id: "" };

export default function UsersTab() {
  const { user: me, branches } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: r }] = await Promise.all([
        api.get("/users"),
        api.get("/roles"),
      ]);
      setUsers(u || []);
      setRoles((r || []).filter((role) => role.name !== "Super Admin"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    if (!form.name || !form.email || !form.password || !form.role_id) {
      toast.error("Name, email, password and role are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, { is_active: !u.is_active });
      toast.success(`${u.name} ${u.is_active ? "disabled" : "enabled"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update status");
    }
  };

  const resetPassword = async () => {
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${pwUser.name}`);
      setPwUser(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="users-tab">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-secondary">User Logins</h3>
          <p className="text-sm text-muted-foreground">Only admins can create and manage login accounts.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setForm(EMPTY); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button"><UserPlus className="mr-1.5 h-4 w-4" /> Add User</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle>Create a user login</DialogTitle>
              <DialogDescription>The user will sign in with this email and password.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@sifcore.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password</Label>
                <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="user-branch-select"><SelectValue placeholder="Head Office" /></SelectTrigger>
                    <SelectContent>
                      {branches.filter((b) => b.id).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={createUser} disabled={saving} data-testid="save-user-button">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Create user
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Branch</th>
              <th className="px-4 py-2.5">Active</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => {
              const isSelf = me?.id === u.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-secondary">
                    {u.name} {isSelf && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge className="border-0 bg-primary/10 text-primary">
                      {u.role_name === "Admin" && <ShieldCheck className="mr-1 h-3 w-3" />}{u.role_name || u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.branch_name || "—"}</td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={u.is_active}
                      disabled={isSelf}
                      onCheckedChange={() => toggleStatus(u)}
                      data-testid={`user-status-toggle-${u.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setPwUser(u); setNewPw(""); }} data-testid={`reset-password-${u.id}`}>
                      <KeyRound className="mr-1 h-3.5 w-3.5" /> Reset password
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={Boolean(pwUser)} onOpenChange={(o) => { if (!o) { setPwUser(null); setNewPw(""); } }}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {pwUser?.name} ({pwUser?.email}).</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input data-testid="reset-password-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 8 characters" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={saving} data-testid="confirm-reset-password">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
